import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { fetchLaunches, fetchNewLaunchInfo } from "../data/launches";
import { fetchHomeInfo } from "../data/home";
import { fetchRocketFamilies } from "../data/rocketFamilies";
import { fetchOrbits } from "../data/orbits";
import { fetchPads } from "../data/pads.js";
import { fetchZones } from "../data/recoveryZones.js";
import { fetchStages } from "../data/stages.js";
import {
  allValuesTrue,
  changeTimeToLocal,
  formatTime,
  getPhotoLocation,
  makeRecoveryString,
  numberWithCommas,
  setAllValuesTrue,
  toTitleCase,
  useDebounce,
  versionFormat,
  customTheme,
  repairMojibake,
} from "../utils";
import "./LaunchList.css";
import { FilterIcon } from "@heroicons/react/outline";
import { ThemeProvider, FormControl, InputLabel, Select, MenuItem } from "@mui/material";

// Helper Functions
const getQueryParam = (param) => {
  const params = new URLSearchParams(window.location.search);
  return params.get(param) || "";
};

const getPaginationParam = () => {
  const page = getQueryParam("page");
  return Number(page) || 1;
};

const getOrderByParam = () => {
  const order = getQueryParam("order");
  return order || "-time";
};

const getFilterParam = () => {
  const filterParam = getQueryParam("filter");
  if (filterParam) {
    try {
      return normalizeLaunchFilter(JSON.parse(decodeURIComponent(filterParam)));
    } catch (error) {
      console.error("Error parsing filter from URL:", error);
    }
  }
  return null;
};

const cloneFilter = (filter) => JSON.parse(JSON.stringify(filter));

function normalizeLaunchFilter(filter) {
  if (filter?.hide__stageandrecovery__method) {
    if (filter.hide__stageandrecovery__method.CATCH === undefined) {
      filter.hide__stageandrecovery__method.CATCH = true;
    }
  }

  return filter;
}

const setAllBooleanChildren = (obj, value) => {
  if (!obj || typeof obj !== "object") {
    return;
  }

  Object.keys(obj).forEach((key) => {
    if (obj[key] && typeof obj[key] === "object") {
      setAllBooleanChildren(obj[key], value);
    } else if (typeof obj[key] === "boolean") {
      obj[key] = value;
    }
  });
};

// Backend ORs landing-zone and recovery-method filters. A subset of one
// must zero the other, or missions with no zone (Falcon 1, expended, etc.) leak in.
const syncRecoveryFilterPair = (nextFilter, selectedGroupKey) => {
  if (!nextFilter) {
    return;
  }

  if (selectedGroupKey === "stageandrecovery__landing_zone" && nextFilter.hide__stageandrecovery__method) {
    setAllBooleanChildren(nextFilter.hide__stageandrecovery__method, allValuesTrue(nextFilter.stageandrecovery__landing_zone));
  }

  if (selectedGroupKey === "hide__stageandrecovery__method" && nextFilter.stageandrecovery__landing_zone) {
    setAllBooleanChildren(nextFilter.stageandrecovery__landing_zone, allValuesTrue(nextFilter.hide__stageandrecovery__method));
  }
};

const countDisabledFilters = (obj) => {
  if (!obj || typeof obj !== "object") {
    return 0;
  }

  return Object.values(obj).reduce((count, value) => {
    if (typeof value === "boolean") {
      return count + (value ? 0 : 1);
    }

    return count + countDisabledFilters(value);
  }, 0);
};

const setImmediateBooleanChildren = (obj, selectedKeys) => {
  if (!obj || typeof obj !== "object") {
    return;
  }

  Object.keys(obj).forEach((key) => {
    if (typeof obj[key] === "boolean") {
      obj[key] = selectedKeys ? selectedKeys.includes(key) : true;
    }
  });
};

const setNestedGroupSelection = (obj, selectedKey) => {
  if (!obj || typeof obj !== "object") {
    return;
  }

  Object.keys(obj).forEach((key) => {
    setAllBooleanChildren(obj[key], selectedKey ? key === selectedKey : true);
  });
};

const immediateBooleansMatch = (obj, selectedKeys) => {
  if (!obj || typeof obj !== "object") {
    return false;
  }

  const selected = new Set(selectedKeys || []);
  const booleanKeys = Object.keys(obj).filter((key) => typeof obj[key] === "boolean");

  return booleanKeys.length > 0 && booleanKeys.every((key) => obj[key] === selected.has(key));
};

const immediateBooleansAll = (obj, value) => {
  if (!obj || typeof obj !== "object") {
    return false;
  }

  const booleanKeys = Object.keys(obj).filter((key) => typeof obj[key] === "boolean");

  return booleanKeys.length > 0 && booleanKeys.every((key) => obj[key] === value);
};

const nestedGroupMatches = (obj, selectedKey) => {
  if (!obj || typeof obj !== "object") {
    return false;
  }

  return Object.keys(obj).every((key) => allValuesTrue(obj[key]) === (selectedKey ? key === selectedKey : true));
};

const getOutcomeLabel = (outcome) => toTitleCase(outcome || "TBD").replace("Tbd", "TBD");

const getOutcomeClass = (outcome) => {
  const normalized = String(outcome || "").toLowerCase();

  if (normalized.includes("success")) {
    return "success";
  }

  if (normalized.includes("partial")) {
    return "partial";
  }

  if (normalized.includes("fail")) {
    return "failure";
  }

  return "tbd";
};

const getAdvancedGroupLabel = (key, filterNames) => {
  const normalizedKey = key.replace("hide__", "");
  return filterNames[key] || filterNames[normalizedKey] || versionFormat(toTitleCase(normalizedKey.replace(/__/g, " ")));
};

const getAdvancedOptionLabel = (path, key, objects) => {
  const parentKey = path.length > 1 ? path[path.length - 2].replace("hide__", "") : path[0].replace("hide__", "");
  const sourceObjects = objects[parentKey] || [];
  const matchedObject = sourceObjects.find((object) => {
    if (object && typeof object === "object") {
      return String(object.id) === String(key);
    }

    return String(object) === String(key);
  });

  if (matchedObject && typeof matchedObject === "object") {
    return matchedObject.nickname || repairMojibake(matchedObject.name) || String(key);
  }

  if (matchedObject) {
    return String(matchedObject);
  }

  return versionFormat(toTitleCase(String(key).replace(/_/g, " "))).replace(/Tbd/g, "TBD");
};

const collectAdvancedOptions = (filtersObj, currentPath, objects) => {
  if (!filtersObj || typeof filtersObj !== "object") {
    return [];
  }

  return Object.keys(filtersObj).flatMap((key) => {
    const value = filtersObj[key];
    const nextPath = [...currentPath, key];

    if (typeof value === "boolean") {
      return [
        {
          key: nextPath.join("|"),
          label: getAdvancedOptionLabel(nextPath, key, objects),
          path: nextPath,
          selected: value,
        },
      ];
    }

    return collectAdvancedOptions(value, nextPath, objects);
  });
};

const getAdvancedFilterGroups = (filter, objects, filterNames) => {
  if (!filter) {
    return [];
  }

  return Object.keys(filter)
    .map((key) => ({
      key,
      label: getAdvancedGroupLabel(key, filterNames),
      options: collectAdvancedOptions(filter[key], [key], objects),
    }))
    .filter((group) => group.options.length > 0);
};

const normalizeSearchValue = (value) => String(value || "").toLowerCase().trim();

const compactSearchValue = (value) => normalizeSearchValue(value).replace(/[^a-z0-9]/g, "");

const getLaunchLandingZoneIds = (launch) => {
  const recoveries = launch.stage_and_recoveries?.length ? launch.stage_and_recoveries : launch.recoveries || [];
  return recoveries
    .map((recovery) => {
      const zone = recovery?.landing_zone;
      if (zone && typeof zone === "object") {
        return zone.id;
      }
      return zone;
    })
    .filter((id) => id != null);
};

const launchMatchesSearch = (launch, searchQuery, { rockets, rocketFamilies, pads, orbits, zones }) => {
  const normalizedQuery = normalizeSearchValue(searchQuery);

  if (!normalizedQuery) {
    return true;
  }

  const compactQuery = compactSearchValue(searchQuery);
  const rocket = rockets.find((item) => String(item.id) === String(launch.rocket));
  const rocketFamily = rocketFamilies.find(
    (family) => String(family.id) === String(rocket?.familyId || rocket?.family)
  );
  const pad = pads.find((item) => String(item.id) === String(launch.pad));
  const orbit = orbits.find((item) => String(item.id) === String(launch.orbit));
  const landingZones = getLaunchLandingZoneIds(launch)
    .map((id) => (zones || []).find((zone) => String(zone.id) === String(id)))
    .filter(Boolean);
  const searchableValues = [
    launch.name,
    launch.customer,
    launch.stages_string,
    rocket?.name,
    rocket?.nickname,
    rocketFamily?.name,
    pad?.name,
    pad?.nickname,
    orbit?.name,
    orbit?.nickname,
    repairMojibake(orbit?.name),
    ...landingZones.flatMap((zone) => [zone.name, zone.nickname]),
  ];
  const haystack = searchableValues.map(normalizeSearchValue).join(" ");
  const compactHaystack = compactSearchValue(searchableValues.join(" "));

  return haystack.includes(normalizedQuery) || (compactQuery && compactHaystack.includes(compactQuery));
};

const formatPayloadMass = (mass) =>
  mass !== null && mass !== undefined ? `${numberWithCommas(mass)} kg` : "Unknown";

const formatTurnaroundDays = (value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  // Detail endpoints return human strings like "356 days, 15 hours, ..."
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return "—";
    }

    if (/^\d+(\.\d+)?$/.test(trimmed)) {
      value = Number(trimmed);
    } else {
      const days = Number((trimmed.match(/(\d+)\s*days?/i) || [])[1] || 0);
      const hours = Number((trimmed.match(/(\d+)\s*hours?/i) || [])[1] || 0);
      const minutes = Number((trimmed.match(/(\d+)\s*minutes?/i) || [])[1] || 0);

      if (!days && !hours && !minutes) {
        return "—";
      }

      const total = Math.round((days + hours / 24 + minutes / 1440) * 100) / 100;
      return `${total} day${total === 1 ? "" : "s"}`;
    }
  }

  const seconds = Number(value);
  if (Number.isNaN(seconds)) {
    return "—";
  }

  const days = Math.round((seconds / 86400) * 100) / 100;
  return `${days} day${days === 1 ? "" : "s"}`;
};

const formatFlightCount = (count) => {
  if (count === null || count === undefined || Number.isNaN(Number(count))) {
    return "—";
  }

  return String(Number(count));
};

const getLaunchRecoveries = (launch) =>
  launch.stage_and_recoveries?.length ? launch.stage_and_recoveries : launch.recoveries || [];

const getPrimaryRecovery = (launch) => getLaunchRecoveries(launch)[0] || null;

const getSpacecraftOnLaunch = (launch) => {
  const entries = Array.isArray(launch.spacecraft_on_launch)
    ? launch.spacecraft_on_launch
    : Array.isArray(launch.spacecraftonlaunch)
      ? launch.spacecraftonlaunch
      : Array.isArray(launch.spacecraft_on_launches)
        ? launch.spacecraft_on_launches
        : [];

  return entries[0] || null;
};

// List serializer omits spacecraft_on_launch; hydrate from launch detail when needed for display.
const SPACECRAFT_ORDER_KEYS = new Set([
  "-spacecraftonlaunch__num_flights",
  "spacecraftonlaunch__spacecraft_turnaround",
]);

const hydrateLaunchesForOrder = async (launches, orderBy) => {
  if (!SPACECRAFT_ORDER_KEYS.has(orderBy) || !launches?.length) {
    return launches;
  }

  const details = await Promise.all(
    launches.map(async (launch) => {
      try {
        return await fetchNewLaunchInfo(launch.id);
      } catch (error) {
        return null;
      }
    })
  );

  return launches.map((launch, index) => {
    const detail = details[index];
    if (!detail) {
      return launch;
    }

    const spacecraftEntries =
      detail.spacecraft_on_launch || detail.spacecraftonlaunch || detail.spacecraft_on_launches || [];

    return {
      ...launch,
      spacecraft_on_launch: spacecraftEntries,
    };
  });
};

const ORDER_BY_FACT_RESOLVERS = {
  "-mass": {
    label: "Payload",
    replaceLast: false,
    getValue: (launch) => formatPayloadMass(launch.mass),
  },
  pad_turnaround: {
    label: "Pad turnaround",
    replaceLast: true,
    getValue: (launch) => formatTurnaroundDays(launch.pad_turnaround),
  },
  company_turnaround: {
    label: "Company turnaround",
    replaceLast: true,
    getValue: (launch) => formatTurnaroundDays(launch.company_turnaround),
  },
  stageandrecovery__stage_turnaround: {
    label: "Stage turnaround",
    replaceLast: true,
    getValue: (launch) => formatTurnaroundDays(getPrimaryRecovery(launch)?.stage_turnaround),
  },
  "stageandrecovery__-num_flights": {
    label: "Stage flights",
    replaceLast: true,
    getValue: (launch) => formatFlightCount(getPrimaryRecovery(launch)?.num_flights),
  },
  stageandrecovery__zone_turnaround: {
    label: "Zone turnaround",
    replaceLast: true,
    getValue: (launch) => formatTurnaroundDays(getPrimaryRecovery(launch)?.zone_turnaround),
  },
  "-spacecraftonlaunch__num_flights": {
    label: "Spacecraft flights",
    replaceLast: true,
    getValue: (launch) => formatFlightCount(getSpacecraftOnLaunch(launch)?.num_flights),
  },
  spacecraftonlaunch__spacecraft_turnaround: {
    label: "Spacecraft turnaround",
    replaceLast: true,
    getValue: (launch) => formatTurnaroundDays(getSpacecraftOnLaunch(launch)?.spacecraft_turnaround),
  },
};

const getLaunchRecovery = (launch, zones) => {
  const recoveries = getLaunchRecoveries(launch);

  if (!recoveries.length) {
    return "TBD";
  }

  const hasKnownRecovery = recoveries.some((recovery) => recovery.landing_zone || recovery.method !== "TBD");

  if (!hasKnownRecovery) {
    return "TBD";
  }

  return makeRecoveryString(recoveries, zones)
    .replace(/Tbd/g, "TBD")
    .replace(/Drone Ship/g, "ASDS");
};

const STATIC_MEDIA_HOST = "https://boostertracker-static.s3.amazonaws.com";
const STARSHIP_FALLBACK_IMAGE = "/media/rocket_pad_photos/Starship_OLPA_launches_photo.jpg";

const FALCON_STOCK_IMAGES = [
  "/media/rocket_pad_photos/Falcon_LC39A_launches_photo.jpg",
  "/media/rocket_pad_photos/Falcon_SLC40_launches_photo.jpg",
  "/media/rocket_pad_photos/Falcon_SLC4E_launches_photo.jpg",
  "/media/rocket_pad_photos/Falcon_Heavy_launches_photo.jpg",
  "/media/rocket_photos/falcon9.jpeg",
  "/media/rocket_photos/Falconheavy.jpeg",
  "/media/pad_photos/lc39a.jpeg",
  "/media/pad_photos/slc_40.jpeg",
  "/media/pad_photos/slc_4e.jpeg",
];

const STARSHIP_STOCK_IMAGES = [
  "/media/rocket_pad_photos/Starship_OLPA_launches_photo.jpg",
  "/media/rocket_photos/Starship.jpeg",
  "/media/pad_photos/IMG_4349.jpeg",
  "/media/pad_photos/olp_a_qknNfEa.jpeg",
];

const cleanLaunchImagePath = (image) => {
  if (!image) {
    return "";
  }

  return String(image)
    .replace(/^http:\/\/127\.0\.0\.1:8000\/?/, "/")
    .replace(/^https?:\/\/boostertracker-static\.s3\.amazonaws\.com/i, "");
};

const isFalconPadFallback = (image) =>
  /\/media\/rocket_pad_photos\/Falcon_/i.test(String(image || ""));

const isPadStockImage = (image) => /\/media\/rocket_pad_photos\//i.test(cleanLaunchImagePath(image));

const isGenericStageImage = (image) =>
  !image || /\/media\/stage_photos\/default_booster\.jpg$/i.test(cleanLaunchImagePath(image));

const hashString = (value) => {
  const str = String(value || "");
  let hash = 0;

  for (let index = 0; index < str.length; index += 1) {
    hash = (hash * 31 + str.charCodeAt(index)) >>> 0;
  }

  return hash;
};

const resolveMediaUrl = (image) => {
  const imagePath = cleanLaunchImagePath(image);

  if (!imagePath) {
    return "";
  }

  if (/^https?:\/\//.test(imagePath)) {
    return imagePath;
  }

  const mediaPath = imagePath.startsWith("/") ? imagePath : `/${imagePath}`;

  if (mediaPath.startsWith("/media/")) {
    if (typeof window !== "undefined" && window.location.hostname === "localhost") {
      return `${getPhotoLocation()}${mediaPath}`;
    }
    return `${STATIC_MEDIA_HOST}${mediaPath}`;
  }

  return `${getPhotoLocation()}${mediaPath}`;
};

const pickLaunchStockImage = (launch, rocket) => {
  const isStarship = rocket?.name === "Starship";
  // Family-scoped pools only — Falcon never draws from Starship art and vice versa.
  const pool = isStarship ? STARSHIP_STOCK_IMAGES : FALCON_STOCK_IMAGES;
  const rocketImage = rocket?.image ? cleanLaunchImagePath(rocket.image) : "";
  const options =
    rocketImage && !pool.includes(rocketImage) && (isStarship || !/starship/i.test(rocketImage))
      ? [rocketImage, ...pool]
      : [...pool];
  const index = hashString(launch?.id ?? launch?.name) % Math.max(options.length, 1);

  return options[index] || pool[0];
};

/**
 * Resolve a launch list thumb.
 * Prefer unique booster photos when the launch only has generic pad stock art —
 * that breaks up long runs of the same pad photo without dropping real booster
 * portraits. Unique mission photos and unique booster images are never replaced.
 * Boosters that only have default_booster.jpg are skipped (fall through to pad art).
 */
const getLaunchImageUrl = (launch, rocket, { pads = [], stagesById = {} } = {}) => {
  const recoveries = getLaunchRecoveries(launch);
  const pad = pads.find((item) => String(item.id) === String(launch?.pad));

  // Prefer unique booster images for missions that only have pad stock art.
  if (!launch?.image || isPadStockImage(launch.image) || isFalconPadFallback(launch.image)) {
    for (const recovery of recoveries) {
      const stageId = recovery?.stage?.id ?? recovery?.stage;
      const stage = recovery?.stage?.image ? recovery.stage : stagesById[String(stageId)];
      const stageImage = stage?.image;

      if (stageImage && !isGenericStageImage(stageImage)) {
        return resolveMediaUrl(stageImage);
      }
    }
  }

  // Real mission-specific photo (not pad stock).
  if (launch?.image && !isPadStockImage(launch.image) && !isFalconPadFallback(launch.image)) {
    return resolveMediaUrl(launch.image);
  }

  if (rocket?.name === "Starship" && (!launch?.image || isFalconPadFallback(launch.image))) {
    return resolveMediaUrl(STARSHIP_FALLBACK_IMAGE);
  }

  // Keep pad-accurate stock art when no unique booster/mission photo exists.
  // Diversify only when even that is missing (pure rocket fallback would otherwise
  // make entire pages look identical).
  if (launch?.image) {
    return resolveMediaUrl(launch.image);
  }

  if (pad?.image) {
    return resolveMediaUrl(pad.image);
  }

  if (rocket?.image) {
    return resolveMediaUrl(rocket.image);
  }

  return resolveMediaUrl(pickLaunchStockImage(launch, rocket));
};

const normalizeLaunchTime = (time) => (time ? String(time).replace(/Z$/, "") : time);

const mergeLaunchDetail = (launch, detail) => {
  if (!launch && !detail) {
    return null;
  }

  const merged = { ...(launch || {}), ...(detail || {}) };

  return {
    ...merged,
    image: detail?.image || launch?.image || merged.image,
    time: normalizeLaunchTime(detail?.time || launch?.time),
  };
};

const LaunchPagination = ({ currentPage, totalPages, onPageChange, className = "" }) => (
  <div className={`ll-pagination ${className}`}>
    <button
      className="ll-icon-button"
      onClick={() => onPageChange(-currentPage + 1)}
      disabled={currentPage === 1}
      aria-label="First page"
      type="button"
    >
      «
    </button>
    <button
      className="ll-icon-button"
      onClick={() => onPageChange(-1)}
      disabled={currentPage === 1}
      aria-label="Previous page"
      type="button"
    >
      ‹
    </button>
    <span>
      Page {currentPage} / {totalPages}
    </span>
    <button
      className="ll-icon-button"
      onClick={() => onPageChange(1)}
      disabled={currentPage >= totalPages}
      aria-label="Next page"
      type="button"
    >
      ›
    </button>
    <button
      className="ll-icon-button"
      onClick={() => onPageChange(totalPages - currentPage)}
      disabled={currentPage >= totalPages}
      aria-label="Last page"
      type="button"
    >
      »
    </button>
  </div>
);

const getStageIdFromRecovery = (recovery) => {
  if (recovery?.stage == null) {
    return null;
  }

  if (typeof recovery.stage === "object") {
    return recovery.stage.id ?? null;
  }

  return recovery.stage;
};

const formatStageFlightLabel = (stage, numFlights) => {
  const name = stage?.name || "";
  const flightNumber = Number(numFlights || stage?.num_launches);

  if (!name) {
    return "";
  }

  if (Number.isFinite(flightNumber) && flightNumber > 0 && !new RegExp(`-${flightNumber}$`).test(name)) {
    return `${name}-${flightNumber}`;
  }

  return name;
};

const getLaunchStageEntries = (launch, stagesById = {}) => {
  const recoveries = getLaunchRecoveries(launch);
  const entries = recoveries
    .map((recovery) => {
      const stageId = getStageIdFromRecovery(recovery);
      const stageFromRecovery = typeof recovery.stage === "object" ? recovery.stage : null;
      const stage = stageFromRecovery?.name ? stageFromRecovery : stagesById[String(stageId)] || stageFromRecovery;
      const label = formatStageFlightLabel(stage, recovery.num_flights);

      if (!label && !stageId) {
        return null;
      }

      return {
        id: stageId,
        label: label || launch.stages_string || "Unknown",
      };
    })
    .filter(Boolean);

  if (entries.length) {
    return entries;
  }

  if (launch.stages_string && launch.stages_string !== "Unknown") {
    return [{ id: null, label: launch.stages_string }];
  }

  return [{ id: null, label: "Unknown" }];
};

const LaunchFact = ({ label, value, isSortKey = false, links = null }) => (
  <span className={`ll-launch-fact${isSortKey ? " is-sort-key" : ""}`}>
    <small>{label}</small>
    <strong>
      {links?.length ? (
        <span className="ll-stage-links">
          {links.map((entry, index) => (
            <React.Fragment key={`${entry.id || entry.label}-${index}`}>
              {index > 0 ? ", " : null}
              {entry.id ? (
                <Link
                  to={`/stage/${entry.id}/`}
                  className="bt-stage-link"
                  onClick={(event) => event.stopPropagation()}
                >
                  {entry.label}
                </Link>
              ) : (
                entry.label
              )}
            </React.Fragment>
          ))}
        </span>
      ) : (
        value || "Unknown"
      )}
    </strong>
  </span>
);

const buildLaunchMetaFacts = (launch, { rocket, pad, recovery, orderBy, stageEntries }) => {
  // Booster-first: stage serial is the primary hardware identity for most users.
  const facts = [
    { label: "Stage", value: stageEntries?.map((entry) => entry.label).join(", "), links: stageEntries },
    { label: "Rocket", value: rocket?.name },
    { label: "Pad", value: pad?.nickname },
    { label: "Recovery", value: recovery },
    { label: "Payload", value: formatPayloadMass(launch.mass) },
  ];

  const resolver = ORDER_BY_FACT_RESOLVERS[orderBy];

  if (!resolver) {
    return facts;
  }

  const sortFact = {
    label: resolver.label,
    value: resolver.getValue(launch),
    isSortKey: true,
  };

  if (resolver.replaceLast) {
    facts[facts.length - 1] = sortFact;
  } else {
    facts[facts.length - 1] = { ...facts[facts.length - 1], isSortKey: true };
  }

  return facts;
};

const LaunchMissionCard = ({ launch, rockets, pads, zones, stagesById = {}, orderBy = "-time" }) => {
  const rocket = rockets.find((item) => String(item.id) === String(launch.rocket));
  const pad = pads.find((item) => String(item.id) === String(launch.pad));
  const outcomeLabel = getOutcomeLabel(launch.launch_outcome);
  const recovery = getLaunchRecovery(launch, zones);
  const imageUrl = getLaunchImageUrl(launch, rocket, { pads, stagesById });
  const stageEntries = getLaunchStageEntries(launch, stagesById);
  const metaFacts = buildLaunchMetaFacts(launch, { rocket, pad, recovery, orderBy, stageEntries });

  return (
    <article className="ll-launch-card ll-launch-link">
      <Link to={`/launch/${launch.id}/`} className="ll-launch-hit" aria-label={`View ${launch.name} launch details`} />
      <span className="ll-launch-thumb" style={{ backgroundImage: imageUrl ? `url(${imageUrl})` : undefined }}></span>
      <span className="ll-launch-main">
        <span className="ll-launch-kicker">
          <span className={`ll-outcome ${getOutcomeClass(launch.launch_outcome)}`}>{outcomeLabel}</span>
          <span>{formatTime(launch.time)}</span>
        </span>
        <strong className="ll-launch-title">{launch.name}</strong>
        <span className="ll-launch-meta">
          {metaFacts.map((fact) => (
            <LaunchFact
              key={fact.label}
              label={fact.label}
              value={fact.value}
              isSortKey={fact.isSortKey}
              links={fact.links}
            />
          ))}
        </span>
      </span>
    </article>
  );
};

const LaunchAdvancedFilters = ({ groups, expandedGroups, onToggleGroup, onToggleOption, onSelectAll }) => (
  <div className="ll-advanced-grid">
    {groups.map((group) => {
      const isExpanded = Boolean(expandedGroups[group.key]);
      const optionLimit = group.options.length > 14 ? 10 : 14;
      const selectedCount = group.options.filter((option) => option.selected).length;
      const allSelected = selectedCount === group.options.length;
      const visibleOptions = isExpanded ? group.options : group.options.slice(0, optionLimit);
      const hiddenCount = group.options.length - visibleOptions.length;

      return (
        <section className="ll-advanced-group" key={group.key}>
          <div className="ll-advanced-group-head">
            <span>{group.label}</span>
            <small className={allSelected ? "is-empty" : ""} aria-hidden={allSelected}>
              {allSelected ? `${group.options.length}/${group.options.length}` : `${selectedCount}/${group.options.length}`}
            </small>
          </div>
          <div className="ll-advanced-options">
            <button
              className={`ll-advanced-option ${allSelected ? "selected" : ""}`}
              onClick={() => onSelectAll(group.key)}
              type="button"
              aria-pressed={allSelected}
            >
              <span aria-hidden="true"></span>
              All
            </button>
            {visibleOptions.map((option) => (
              <button
                className={`ll-advanced-option ${option.selected && !allSelected ? "selected" : ""}`}
                key={option.key}
                onClick={() => onToggleOption(group.key, option.path, allSelected)}
                type="button"
                aria-pressed={option.selected && !allSelected}
              >
                <span aria-hidden="true"></span>
                {option.label}
              </button>
            ))}
            {hiddenCount > 0 ? (
              <button className="ll-advanced-more" onClick={() => onToggleGroup(group.key)} type="button">
                +{hiddenCount} more
              </button>
            ) : group.options.length > optionLimit ? (
              <button className="ll-advanced-more" onClick={() => onToggleGroup(group.key)} type="button">
                Show less
              </button>
            ) : null}
          </div>
        </section>
      );
    })}
  </div>
);

const LaunchList = () => {
  const [query, setQuery] = useState(getQueryParam("query"));
  const [inputValue, setInputValue] = useState(query);
  const [launches, setLaunches] = useState([]);
  const [filter, setFilter] = useState(getFilterParam());
  const [currentPage, setCurrentPage] = useState(getPaginationParam());
  const [orderBy, setOrderBy] = useState(getOrderByParam());
  const [numLaunches, setNumLaunches] = useState(0);
  const [rocketFamilies, setRocketFamilies] = useState([]);
  const [rockets, setRockets] = useState([]);
  const [orbits, setOrbits] = useState([]);
  const [pads, setPads] = useState([]);
  const [zones, setZones] = useState([]);
  const [stagesById, setStagesById] = useState({});
  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [expandedAdvancedGroups, setExpandedAdvancedGroups] = useState({});
  const [loading, setLoading] = useState(false);
  const [nextMission, setNextMission] = useState(null);
  const [nextMissionLoading, setNextMissionLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const launchesPerPage = 25;
  const debouncedFilter = useDebounce(filter, 0);

  const objectsData = {
    orbit: orbits,
    rocket__family: rocketFamilies,
    rocket: rockets,
    launch_outcome: ["Success", "Failure", "Partial Failure"],
    pad: pads,
    stageandrecovery__landing_zone: zones,
  };

  const filterNames = {
    launch_outcome: "Outcome",
    rocket__family: "Rocket Family",
    stageandrecovery__landing_zone: "Landing Method",
    orbit: "Orbit",
    pad: "Pad",
    hide__stageandrecovery__method: "Recovery Method",
    OCEAN_SURFACE: "Ocean",
    stageandrecovery__method_success: "Landing Method Outcome",
  };

  const totalPages = Math.max(1, Math.ceil(numLaunches / launchesPerPage));
  const disabledFilterCount = useMemo(() => countDisabledFilters(filter), [filter]);
  const advancedFilterGroups = getAdvancedFilterGroups(filter, objectsData, filterNames);

  const quickFilterGroups = useMemo(() => {
    const falconFamily = rocketFamilies.find((family) => family.name === "Falcon");
    const starshipFamily = rocketFamilies.find((family) => family.name === "Starship");
    const padsByNickname = Object.fromEntries(pads.map((pad) => [pad.nickname, pad]));
    const padOptions = [
      ["LC-39A", padsByNickname["LC-39A"]],
      ["SLC-40", padsByNickname["SLC-40"]],
      ["SLC-4E", padsByNickname["SLC-4E"]],
      ["Starbase", padsByNickname["OLP-A"]],
    ].filter(([, pad]) => pad);

    const groups = [
      {
        label: "Vehicle",
        options: [
          {
            label: "All",
            apply: (nextFilter) => setNestedGroupSelection(nextFilter.rocket__family),
            isActive: (currentFilter) => nestedGroupMatches(currentFilter?.rocket__family),
          },
          falconFamily && {
            label: "Falcon",
            apply: (nextFilter) => setNestedGroupSelection(nextFilter.rocket__family, String(falconFamily.id)),
            isActive: (currentFilter) => nestedGroupMatches(currentFilter?.rocket__family, String(falconFamily.id)),
          },
          starshipFamily && {
            label: "Starship",
            apply: (nextFilter) => setNestedGroupSelection(nextFilter.rocket__family, String(starshipFamily.id)),
            isActive: (currentFilter) => nestedGroupMatches(currentFilter?.rocket__family, String(starshipFamily.id)),
          },
        ].filter(Boolean),
      },
      {
        label: "Mission status",
        options: [
          {
            label: "All",
            apply: (nextFilter) => setImmediateBooleanChildren(nextFilter.launch_outcome),
            isActive: (currentFilter) => immediateBooleansMatch(currentFilter?.launch_outcome, [
              "Success",
              "Failure",
              "Partial Failure",
              "TBD",
            ]),
          },
          {
            label: "Upcoming",
            apply: (nextFilter) => setImmediateBooleanChildren(nextFilter.launch_outcome, ["TBD"]),
            isActive: (currentFilter) => immediateBooleansMatch(currentFilter?.launch_outcome, ["TBD"]),
          },
          {
            label: "Successful",
            apply: (nextFilter) => setImmediateBooleanChildren(nextFilter.launch_outcome, ["Success"]),
            isActive: (currentFilter) => immediateBooleansMatch(currentFilter?.launch_outcome, ["Success"]),
          },
          {
            label: "Failures",
            apply: (nextFilter) => setImmediateBooleanChildren(nextFilter.launch_outcome, ["Failure", "Partial Failure"]),
            isActive: (currentFilter) =>
              immediateBooleansMatch(currentFilter?.launch_outcome, ["Failure", "Partial Failure"]),
          },
        ],
      },
      {
        label: "Launch site",
        options: [
          {
            label: "All",
            apply: (nextFilter) => setImmediateBooleanChildren(nextFilter.pad),
            isActive: (currentFilter) => immediateBooleansAll(currentFilter?.pad, true),
          },
          ...padOptions.map(([label, pad]) => ({
            label,
            apply: (nextFilter) => setImmediateBooleanChildren(nextFilter.pad, [String(pad.id)]),
            isActive: (currentFilter) => immediateBooleansMatch(currentFilter?.pad, [String(pad.id)]),
          })),
        ],
      },
    ];

    return groups.filter((group) => group.options.length > 1);
  }, [pads, rocketFamilies]);

  useEffect(() => {
    (async () => {
      const [rocketFamilyData, orbitData, padData, zoneData, falconStages, starshipStages] = await Promise.all([
        fetchRocketFamilies(),
        fetchOrbits(),
        fetchPads("", {}),
        fetchZones("", {}),
        fetchStages("", null, "falcon", "BOOSTER").catch(() => ({ stages: [] })),
        fetchStages("", null, "starship", "BOOSTER").catch(() => ({ stages: [] })),
      ]);
      setOrbits(orbitData);
      setPads(padData);
      setZones(zoneData);
      setRocketFamilies(rocketFamilyData);

      const rocketsInData = rocketFamilyData.flatMap((family) =>
        family.rockets.map((rocket) => ({ ...rocket, familyId: family.id }))
      );

      setRockets(rocketsInData);

      // Index stages for unique booster photos used in launch thumbs.
      const stageIndex = {};
      [...(falconStages?.stages || []), ...(starshipStages?.stages || [])].forEach((stage) => {
        if (stage?.id != null) {
          stageIndex[String(stage.id)] = stage;
        }
      });
      setStagesById(stageIndex);

      setDataLoaded(true);
    })();
  }, []);

  const fetchInitialFilter = useCallback(async () => {
    if (filter || !dataLoaded) {
      return;
    }

    const initialRocketFamilyFilter = rocketFamilies.reduce((acc, family) => {
      acc[family.id] = { hide__rocket: Object.fromEntries(family.rockets.map((rocket) => [rocket.id, true])) };
      return acc;
    }, {});

    const initialOrbitFilters = Object.fromEntries(orbits.map((orbit) => [orbit.id, true]));
    const initialPadFilters = Object.fromEntries(pads.map((pad) => [pad.id, true]));

    const stageandrecovery__landing_zone = {
      ASDS: { hide__stageandrecovery__landing_zone: {} },
      RTLS: { hide__stageandrecovery__landing_zone: {} },
    };

    const hide__stageandrecovery__method = {
      OCEAN_SURFACE: true,
      PARACHUTE: true,
      CATCH: true,
      expended: true,
    };

    zones.forEach((zone) => {
      if (zone.type === "DRONE_SHIP") {
        stageandrecovery__landing_zone.ASDS.hide__stageandrecovery__landing_zone[zone.id] = true;
      } else if (zone.type === "GROUND_PAD" || zone.type === "CATCH") {
        stageandrecovery__landing_zone.RTLS.hide__stageandrecovery__landing_zone[zone.id] = true;
      }
    });

    setFilter({
      rocket__family: initialRocketFamilyFilter,
      pad: initialPadFilters,
      stageandrecovery__landing_zone: stageandrecovery__landing_zone,
      hide__stageandrecovery__method: hide__stageandrecovery__method,
      stageandrecovery__method_success: {
        SUCCESS: true,
        FAILURE: true,
        PRECLUDED: true,
        TBD: true,
      },
      launch_outcome: {
        Success: true,
        Failure: true,
        "Partial Failure": true,
        TBD: true,
      },
      orbit: initialOrbitFilters,
    });
  }, [dataLoaded, filter, orbits, pads, rocketFamilies, zones]);

  useEffect(() => {
    fetchInitialFilter();
  }, [fetchInitialFilter, dataLoaded]);

  useEffect(() => {
    if (!dataLoaded) {
      return;
    }

    const fetchNextMission = async () => {
      setNextMissionLoading(true);

      try {
        const homeData = await fetchHomeInfo(new Date(new Date().getUTCFullYear() - 2, 0, 1), "exponential");
        const nextLaunch = homeData.next_launch;

        if (!nextLaunch?.id) {
          setNextMission(null);
          return;
        }

        try {
          const detail = await fetchNewLaunchInfo(nextLaunch.id);
          setNextMission(mergeLaunchDetail(nextLaunch, detail));
        } catch (error) {
          setNextMission(mergeLaunchDetail(nextLaunch, null));
        }
      } catch (error) {
        setNextMission(null);
      } finally {
        setNextMissionLoading(false);
      }
    };

    fetchNextMission();
  }, [dataLoaded]);

  useEffect(() => {
    const updateURL = (pageValue = currentPage) => {
      const params = new URLSearchParams(window.location.search);
      params.set("filter", encodeURIComponent(JSON.stringify(debouncedFilter)));
      params.set("page", pageValue);
      params.set("query", query);
      params.set("order", orderBy);
      window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
    };

    const fetchData = async () => {
      if (!debouncedFilter || !dataLoaded) {
        return;
      }

      setLoading(true);
      const trimmedQuery = query.trim();

      try {
        if (!trimmedQuery) {
          updateURL();
          const data = await fetchLaunches("", debouncedFilter, currentPage, orderBy);
          setNumLaunches(data.count);
          const pageLaunches = changeTimeToLocal(data.results || []);
          setLaunches(await hydrateLaunchesForOrder(pageLaunches, orderBy));
          return;
        }

        const firstPage = await fetchLaunches("", debouncedFilter, 1, orderBy);
        const allPageCount = Math.max(1, Math.ceil(firstPage.count / launchesPerPage));
        const restPages =
          allPageCount > 1
            ? await Promise.all(
                Array.from({ length: allPageCount - 1 }, (_, index) =>
                  fetchLaunches("", debouncedFilter, index + 2, orderBy)
                )
              )
            : [];
        const allLaunches = [firstPage, ...restPages].flatMap((page) => page.results || []);
        const matchingLaunches = allLaunches.filter((launch) =>
          launchMatchesSearch(launch, trimmedQuery, { rockets, rocketFamilies, pads, orbits, zones })
        );
        const matchingPageCount = Math.max(1, Math.ceil(matchingLaunches.length / launchesPerPage));
        const safePage = Math.min(currentPage, matchingPageCount);

        if (safePage !== currentPage) {
          setCurrentPage(safePage);
        }

        updateURL(safePage);
        setNumLaunches(matchingLaunches.length);
        const pageLaunches = changeTimeToLocal(
          matchingLaunches.slice((safePage - 1) * launchesPerPage, safePage * launchesPerPage)
        );
        setLaunches(await hydrateLaunchesForOrder(pageLaunches, orderBy));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [query, debouncedFilter, currentPage, orderBy, dataLoaded, rockets, rocketFamilies, pads, orbits, zones]);

  const handlePageChange = useCallback(
    (delta) => {
      setCurrentPage((prev) => Math.max(1, Math.min(prev + delta, totalPages)));
    },
    [totalPages]
  );

  const handleSearch = useCallback(() => {
    setQuery(inputValue);
    setCurrentPage(1);
  }, [inputValue]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        handleSearch();
      }
    },
    [handleSearch]
  );

  const resetFilters = () => {
    const newFilter = filter ? setAllValuesTrue(filter) : filter;
    setFilter(newFilter);
    setQuery("");
    setInputValue("");
    setCurrentPage(1);
    setOrderBy("-time");
  };

  const handleOrderByChange = (event) => {
    setOrderBy(event.target.value);
    setCurrentPage(1);
  };

  const handleQuickFilter = (quickFilter) => {
    if (!filter) {
      return;
    }

    const newFilter = cloneFilter(filter);
    quickFilter.apply(newFilter);
    setFilter(newFilter);
    setCurrentPage(1);
  };

  const handleAdvancedFilterOption = useCallback(
    (groupKey, path, groupWasAllSelected = false) => {
      if (!filter) {
        return;
      }

      const newFilter = cloneFilter(filter);
      if (groupWasAllSelected) {
        setAllBooleanChildren(newFilter[groupKey], false);
      }

      let current = newFilter;

      for (let index = 0; index < path.length - 1; index++) {
        current = current[path[index]];
      }

      const lastKey = path[path.length - 1];
      current[lastKey] = groupWasAllSelected ? true : !current[lastKey];
      syncRecoveryFilterPair(newFilter, groupKey);
      setFilter(newFilter);
      setCurrentPage(1);
    },
    [filter]
  );

  const handleAdvancedFilterAll = useCallback(
    (groupKey) => {
      if (!filter) {
        return;
      }

      const newFilter = cloneFilter(filter);
      setAllBooleanChildren(newFilter[groupKey], true);
      syncRecoveryFilterPair(newFilter, groupKey);
      setFilter(newFilter);
      setCurrentPage(1);
    },
    [filter]
  );

  const handleAdvancedGroupToggle = useCallback((key) => {
    setExpandedAdvancedGroups((previousGroups) => ({
      ...previousGroups,
      [key]: !previousGroups[key],
    }));
  }, []);

  return (
    <div className="launches-page">
      <section className="ll-hero">
        <div>
          <p className="bt-section-label">Launch database</p>
          <h1>Launches</h1>
          <p>Browse upcoming and completed SpaceX missions.</p>
        </div>
        <div className="ll-summary-grid">
          <span>
            <small>Total</small>
            <strong>{numberWithCommas(numLaunches)}</strong>
          </span>
          <span>
            <small>Page</small>
            <strong>
              {currentPage}/{totalPages}
            </strong>
          </span>
          <span>
            <small>Shown</small>
            <strong>{launches.length}</strong>
          </span>
        </div>
      </section>

      <div className="ll-browser-layout">
        <aside className="ll-command-panel">
          <div className="ll-toolbar">
            <label className="ll-search-field">
              <span>Search launches</span>
              <input
                type="text"
                name="q"
                placeholder="Mission, customer, vehicle..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </label>
            <div className="ll-sort-field">
              <ThemeProvider theme={customTheme}>
                <FormControl variant="outlined" margin="none" sx={{ width: "100%" }}>
                  <InputLabel id="launch-order-label">Order By</InputLabel>
                  <Select
                    labelId="launch-order-label"
                    id="launch-order"
                    value={orderBy}
                    onChange={handleOrderByChange}
                    label="Order By"
                    MenuProps={{
                      PaperProps: {
                        className: "ll-sort-menu-paper",
                      },
                      MenuListProps: {
                        className: "ll-sort-menu-list",
                      },
                    }}
                    sx={{ height: "46px" }}
                  >
                    <MenuItem value="-time">Time</MenuItem>
                    <MenuItem value="-mass">Mass</MenuItem>
                    <MenuItem value="pad_turnaround">Pad Turnaround</MenuItem>
                    <MenuItem value="company_turnaround">Company Turnaround</MenuItem>
                    <MenuItem value="stageandrecovery__stage_turnaround">Stage Turnaround</MenuItem>
                    <MenuItem value="stageandrecovery__-num_flights">Stage Flights</MenuItem>
                    <MenuItem value="stageandrecovery__zone_turnaround">Zone Turnaround</MenuItem>
                    <MenuItem value="-spacecraftonlaunch__num_flights">Spacecraft Flights</MenuItem>
                    <MenuItem value="spacecraftonlaunch__spacecraft_turnaround">Spacecraft Turnaround</MenuItem>
                  </Select>
                </FormControl>
              </ThemeProvider>
            </div>
            <div className="ll-toolbar-actions">
              <button onClick={handleSearch} className="ll-action-button primary" type="button">
                Search
              </button>
              <button onClick={resetFilters} className="ll-action-button" type="button">
                Clear
              </button>
            </div>
            <LaunchPagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
          </div>

          <button
            className={`ll-mobile-filter-toggle ${isMobileFiltersOpen ? "open" : ""}`}
            onClick={() => setIsMobileFiltersOpen((prev) => !prev)}
            type="button"
            aria-expanded={isMobileFiltersOpen}
            aria-controls="launch-filter-drawer"
          >
            <FilterIcon aria-hidden="true" />
            <span>{isMobileFiltersOpen ? "Hide filters" : "Show filters"}</span>
            <small className={disabledFilterCount ? "" : "is-empty"} aria-hidden={!disabledFilterCount}>
              {disabledFilterCount || 0}
            </small>
          </button>

          <div className={`ll-filter-drawer ${isMobileFiltersOpen ? "open" : ""}`} id="launch-filter-drawer">
            <div className="ll-filter-presets" aria-label="Quick filters">
              {quickFilterGroups.map((group) => (
                <div className="ll-filter-group" key={group.label}>
                  <span>{group.label}</span>
                  <div>
                    {group.options.map((quickFilter) => {
                      const selected = quickFilter.isActive(filter);

                      return (
                        <button
                          className={selected ? "selected" : ""}
                          key={`${group.label}-${quickFilter.label}`}
                          onClick={() => handleQuickFilter(quickFilter)}
                          type="button"
                          disabled={!filter}
                          aria-pressed={selected}
                        >
                          {quickFilter.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              <button
                className={`ll-advanced-toggle ${isAdvancedFiltersOpen ? "open" : ""}`}
                onClick={() => setIsAdvancedFiltersOpen((prev) => !prev)}
                type="button"
                aria-expanded={isAdvancedFiltersOpen}
                aria-controls="launch-advanced-filters"
              >
                <FilterIcon aria-hidden="true" />
                <span className="ll-advanced-label">Advanced filters</span>
                <span className={`ll-advanced-count ${disabledFilterCount ? "" : "is-empty"}`} aria-hidden={!disabledFilterCount}>
                  {disabledFilterCount || 0}
                </span>
              </button>
            </div>

            {isAdvancedFiltersOpen ? (
              <div className="ll-advanced-panel" id="launch-advanced-filters">
                <LaunchAdvancedFilters
                  groups={advancedFilterGroups}
                  expandedGroups={expandedAdvancedGroups}
                  onToggleGroup={handleAdvancedGroupToggle}
                  onToggleOption={handleAdvancedFilterOption}
                  onSelectAll={handleAdvancedFilterAll}
                />
              </div>
            ) : null}
          </div>
        </aside>

        <div className="ll-main-column">
          <section className="ll-next-panel" aria-busy={nextMissionLoading}>
            <div className="ll-next-head">
              <div>
                <p className="bt-section-label">Next mission</p>
                <h2>{nextMissionLoading ? "Loading mission" : nextMission ? "Coming up next" : "No upcoming mission"}</h2>
              </div>
            </div>
            <div className="ll-next-body">
              {nextMissionLoading ? (
                <div className="loading-indicator"></div>
              ) : nextMission ? (
                <LaunchMissionCard
                  launch={nextMission}
                  rockets={rockets}
                  pads={pads}
                  zones={zones}
                  stagesById={stagesById}
                />
              ) : (
                <p className="ll-empty">No upcoming mission is available.</p>
              )}
            </div>
          </section>

          <section className="ll-results-panel" aria-busy={loading}>
            <div className="ll-results-head">
              <div>
                <p className="bt-section-label">Mission feed</p>
                <h2>{`${numberWithCommas(numLaunches)} matching launches`}</h2>
              </div>
              <LaunchPagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
            </div>

            <div className={`ll-launch-feed ${loading ? "is-updating" : ""}`}>
              {launches.length > 0 ? (
                launches.map((launch) => (
                  <LaunchMissionCard
                    key={launch.id}
                    launch={launch}
                    rockets={rockets}
                    pads={pads}
                    zones={zones}
                    stagesById={stagesById}
                    orderBy={orderBy}
                  />
                ))
              ) : loading ? (
                <div className="loading-indicator"></div>
              ) : (
                <p className="ll-empty">No launches match the current filters.</p>
              )}
            </div>

            <div className="ll-results-foot">
              <span>
                Showing {launches.length} of {numberWithCommas(numLaunches)} launches
              </span>
              <LaunchPagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default LaunchList;
